import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
	ReactFlow,
	Background,
	Controls,
	MiniMap,
	MarkerType,
	Position,
	Handle,
	type Node,
	type Edge,
	type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { networkConnection } from "@/endpoints/dryp/networkConnection";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { ColumnFiltersState } from "@tanstack/react-table";
import { CUSTOM_FILTERS } from "@/config/list";
import { DEFAULT_I_AND_I_TIME_PERIOD, IAndITimePeriodsValue } from "@/config/date";
import { getTableStructureFilterParams } from "@/utils/filters";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Spinner } from "@/components/Spinner";
import { StructureDetailPanel } from "./StructureDetailPanel";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";

const MIN_NODE_DIAMETER = 80;
const MAX_NODE_DIAMETER = 160;

type FlowMetric = "total" | "inflow" | "infiltration" | "waste";

type SummaryValidationStatus = "Valid" | "Invalid" | "NoData" | "Unknown";

// The breakdown metrics that sum into the "total" metric.
const BREAKDOWN_METRICS = ["infiltration", "inflow", "waste"] as const;

const sumDefined = (values: (number | undefined)[]): number | undefined => {
	const defined = values.filter((value): value is number => value != null && Number.isFinite(value));
	return defined.length > 0 ? defined.reduce((sum, value) => sum + value, 0) : undefined;
};

const STATUS_STYLE: Record<SummaryValidationStatus, { color: string; fill: string; label: string; message: string }> = {
	Valid: { color: "#22c55e", fill: "#dcfce7", label: "Valid", message: "Reconciles with upstream." },
	Invalid: {
		color: "#ef4444",
		fill: "#fee2e2",
		label: "Invalid",
		message: "Does not reconcile with upstream.",
	},
	NoData: { color: "#f59e0b", fill: "#fef3c7", label: "No data", message: "No value for this volume to validate." },
	Unknown: { color: "#eab308", fill: "#fefce8", label: "Unknown", message: "Not validated." },
};

const diameterForValue = (value: number | undefined, maxValue: number): number => {
	if (value == null || !Number.isFinite(value) || maxValue <= 0) return MIN_NODE_DIAMETER;
	const ratio = Math.min(1, Math.abs(value) / maxValue);
	return MIN_NODE_DIAMETER + Math.sqrt(ratio) * (MAX_NODE_DIAMETER - MIN_NODE_DIAMETER);
};

const metricValidFlag = (
	validation: Dryp.IAndISummary["validation"],
	metric: FlowMetric,
): boolean | null | undefined => {
	if (!validation) return undefined;
	if (metric === "total") return validation.isTotalVolumeValid;
	if (metric === "inflow") return validation.isRainInflowVolumeValid;
	if (metric === "infiltration") return validation.isInfiltrationVolumeValid;
	return validation.isWasteVolumeValid;
};

const statusOf = (iAndI: Dryp.IAndISummary, metric: FlowMetric): SummaryValidationStatus => {
	const flag = metricValidFlag(iAndI.validation, metric);
	if (flag === undefined) return "Unknown";
	if (flag === null) return "NoData";
	return flag ? "Valid" : "Invalid";
};

interface FlowNodeData {
	name: string;
	valueLabel: string;
	borderColor: string;
	fillColor: string;
	isInvalid: boolean;
	diameter: number;
	[key: string]: unknown;
}

const CircleNode = ({ data }: NodeProps) => {
	const nodeData = data as FlowNodeData;
	return (
		<div
			className="relative flex flex-col items-center justify-center gap-0.5 rounded-full text-center"
			style={{
				width: nodeData.diameter,
				height: nodeData.diameter,
				padding: 10,
				background: nodeData.fillColor,
				border: `3px solid ${nodeData.borderColor}`,
			}}
		>
			{nodeData.isInvalid && (
				<span
					className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] text-white"
					style={{ background: STATUS_STYLE.Invalid.color }}
				>
					⚠
				</span>
			)}
			<Handle type="target" position={Position.Top} style={{ width: 8, height: 8 }} />
			<div className="text-[11px] font-medium leading-tight">{nodeData.name}</div>
			<div className="text-sm font-semibold tabular-nums">{nodeData.valueLabel}</div>
			<Handle type="source" position={Position.Bottom} style={{ width: 8, height: 8 }} />
		</div>
	);
};

const nodeTypes = { iAndIStructure: CircleNode };

const formatValue = (value?: number, unit?: string): string => {
	if (value == null || !Number.isFinite(value)) return "—";
	return `${Math.round(value).toLocaleString()}${unit ? ` ${unit}` : ""}`;
};

const metricValue = (iAndI: Dryp.IAndISummary, metric: FlowMetric): number | undefined => {
	if (metric === "inflow") return iAndI.parts?.inflow.volume?.value;
	if (metric === "infiltration") return iAndI.parts?.infiltration.volume?.value;
	if (metric === "waste") return iAndI.parts?.waste.volume?.value;
	// total = waste + infiltration + inflow volumes.
	return sumDefined([
		iAndI.parts?.waste.volume?.value,
		iAndI.parts?.infiltration.volume?.value,
		iAndI.parts?.inflow.volume?.value,
	]);
};

// Flow carried out of a structure for the active metric — for "total" it sums the breakdown flows.
const metricFlowValue = (iAndI: Dryp.IAndISummary, metric: FlowMetric): number | undefined => {
	if (!iAndI.parts) {
		return undefined;
	}
	if (metric === "total") {
		return sumDefined(
			BREAKDOWN_METRICS.map(breakdownMetric => (iAndI.parts ? iAndI.parts[breakdownMetric].flow?.value : 0)),
		);
	}
	return iAndI.parts[metric].flow?.value;
};

// Units travel inline on every value and are the same across parts/structures,
// so read the symbol straight from the data instead of a hardcoded meta lookup.
const firstUnitSymbol = (
	iAndIStructures: Dryp.IAndISummary[],
	pick: (part: Dryp.IAndISummaryParts["waste"]) => Dryp.IAndISummaryParts["waste"]["volume"],
): string | undefined => {
	for (const iAndI of iAndIStructures) {
		if (!iAndI.parts) continue;
		for (const part of [iAndI.parts.waste, iAndI.parts.infiltration, iAndI.parts.inflow]) {
			const symbol = pick(part)?.unit.symbol;
			if (symbol) return symbol;
		}
	}
	return undefined;
};

const metricUnit = (iAndIStructures: Dryp.IAndISummary[]): string | undefined =>
	firstUnitSymbol(iAndIStructures, part => part.volume);

const metricFlowUnit = (iAndIStructures: Dryp.IAndISummary[]): string | undefined =>
	firstUnitSymbol(iAndIStructures, part => part.flow);

const layoutNodes = (nodes: Node[], edges: Edge[]): Node[] => {
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));
	dagreGraph.setGraph({ rankdir: "TB", nodesep: 50, ranksep: 80, marginx: 40, marginy: 40 });

	nodes.forEach(node => {
		const { diameter } = node.data as FlowNodeData;
		dagreGraph.setNode(node.id, { width: diameter, height: diameter });
	});
	edges.forEach(edge => dagreGraph.setEdge(edge.source, edge.target));
	dagre.layout(dagreGraph);

	return nodes.map(node => {
		const { x, y } = dagreGraph.node(node.id);
		const { diameter } = node.data as FlowNodeData;
		return {
			...node,
			position: { x: x - diameter / 2, y: y - diameter / 2 },
			sourcePosition: Position.Bottom,
			targetPosition: Position.Top,
		};
	});
};

const toFlow = (
	iAndIStructures: Dryp.IAndISummary[],
	connections: Dryp.NetworkConnection[],
	metric: FlowMetric,
): { nodes: Node[]; edges: Edge[] } => {
	const unit = metricUnit(iAndIStructures);
	const flowUnit = metricFlowUnit(iAndIStructures);
	const structureIds = new Set(iAndIStructures.map(iAndI => String(iAndI.structure.id)));

	// Flow carried out of each structure for the active metric — labels the downstream pipe.
	const flowByStructureId = new Map<string, number | undefined>(
		iAndIStructures.map(iAndI => [String(iAndI.structure.id), metricFlowValue(iAndI, metric)]),
	);
	const flowValues = [...flowByStructureId.values()].filter(
		(value): value is number => value != null && Number.isFinite(value),
	);
	const maxFlow = flowValues.length > 0 ? Math.max(...flowValues.map(Math.abs)) : 0;
	const edgeWidth = (flow?: number) => (flow == null || maxFlow <= 0 ? 1.5 : 1 + (Math.abs(flow) / maxFlow) * 4);

	const edges: Edge[] = connections
		.filter(
			connection =>
				structureIds.has(String(connection.fromStructureId)) &&
				structureIds.has(String(connection.toStructureId)),
		)
		.map(connection => {
			const carriedFlow = flowByStructureId.get(String(connection.fromStructureId));
			return {
				id: `${connection.fromStructureId}->${connection.toStructureId}`,
				source: String(connection.fromStructureId),
				target: String(connection.toStructureId),
				animated: carriedFlow != null && carriedFlow > 0,
				label: carriedFlow != null ? formatValue(carriedFlow, flowUnit) : undefined,
				labelStyle: { fontSize: 10, fill: "#475569" },
				labelBgStyle: { fill: "#ffffff", fillOpacity: 0.85 },
				markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
				style: { strokeWidth: edgeWidth(carriedFlow), stroke: "#64748b" },
			};
		});

	const metricValues = iAndIStructures
		.map(iAndI => metricValue(iAndI, metric))
		.filter((value): value is number => value != null && Number.isFinite(value));
	const maxMetricValue = metricValues.length > 0 ? Math.max(...metricValues.map(Math.abs)) : 0;

	const nodes: Node[] = iAndIStructures.map(iAndI => {
		const structureNodeId = String(iAndI.structure.id);
		const status = statusOf(iAndI, metric);
		const value = metricValue(iAndI, metric);
		return {
			id: structureNodeId,
			type: "iAndIStructure",
			position: { x: 0, y: 0 },
			data: {
				name: iAndI.structure.name,
				valueLabel: formatValue(value, unit),
				borderColor: STATUS_STYLE[status].color,
				fillColor: STATUS_STYLE[status].fill,
				isInvalid: status === "Invalid",
				diameter: diameterForValue(value, maxMetricValue),
			},
		};
	});

	return { nodes: layoutNodes(nodes, edges), edges };
};

const METRIC_OPTIONS: { value: FlowMetric; label: string }[] = [
	{ value: "total", label: "Total" },
	{ value: "inflow", label: "Inflow" },
	{ value: "infiltration", label: "Infiltration" },
	{ value: "waste", label: "Waste" },
];

interface IAndIFlowProps {
	columnFilters: ColumnFiltersState;
}

export const IAndIFlow = observer(({ columnFilters }: IAndIFlowProps) => {
	const iandiApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);
	const connectionsApi = useAsyncStateStore(networkConnection.getAll);

	const [metric, setMetric] = useState<FlowMetric>("total");
	const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null);

	const timePeriod =
		(
			columnFilters.find(columnFilter => columnFilter.id === CUSTOM_FILTERS.timePeriod)?.value as
				| IAndITimePeriodsValue[]
				| undefined
		)?.[0] ?? DEFAULT_I_AND_I_TIME_PERIOD;
	const structureFilters = getTableStructureFilterParams(columnFilters);
	const fetchKey = JSON.stringify({ timePeriod, structureFilters });

	useEffect(() => {
		iandiApi.fetch({ pageNumber: 0, pageSize: 100000 }, timePeriod, undefined, structureFilters);
	}, [fetchKey]);

	useEffect(() => {
		connectionsApi.fetch({ pageNumber: 0, pageSize: 2000 });
	}, []);

	const iAndIStructures = iandiApi.data?.items ?? [];
	const connections = connectionsApi.data?.items ?? [];

	const { nodes, edges } = useMemo(
		() => toFlow(iAndIStructures, connections, metric),
		[iAndIStructures, connections, metric],
	);

	const selectedStructure = iAndIStructures.find(iAndI => String(iAndI.structure.id) === selectedStructureId);
	const flowKey = `${metric}-${timePeriod}-${nodes.length}-${edges.length}`;

	const isLoading = iandiApi.loading;
	const isEmpty = !isLoading && iAndIStructures.length === 0;

	const selectedStatus = selectedStructure ? statusOf(selectedStructure, metric) : "Unknown";
	const selectedValidation = selectedStructure?.validation ?? null;

	const structureById = useMemo(
		() => new Map(iAndIStructures.map(iAndI => [String(iAndI.structure.id), iAndI])),
		[iAndIStructures],
	);
	const missingUpstreamCount = selectedValidation
		? selectedValidation.upstreamStructureIds.filter(upstreamId => {
				const upstream = structureById.get(String(upstreamId));
				const upstreamStatus = upstream ? statusOf(upstream, metric) : "Unknown";
				return upstreamStatus === "NoData" || upstreamStatus === "Unknown";
			}).length
		: 0;

	return (
		<div className="relative h-full w-full">
			{isLoading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
					<Spinner size={24} messages={["Loading infiltration & inflow…"]} loaderClassName="text-blue-500" />
				</div>
			)}

			{isEmpty ? (
				<div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
					No infiltration or inflow data found
				</div>
			) : (
				<ReactFlow
					key={flowKey}
					defaultNodes={nodes}
					defaultEdges={edges}
					nodeTypes={nodeTypes}
					nodesConnectable={false}
					onNodeClick={(_event, node) => setSelectedStructureId(node.id)}
					onPaneClick={() => setSelectedStructureId(null)}
					fitView
					proOptions={{ hideAttribution: true }}
				>
					<Background />
					<Controls />
					<MiniMap pannable zoomable />
				</ReactFlow>
			)}

			<div className="absolute left-3 top-3 z-10">
				<ToggleGroup
					type="single"
					value={metric}
					onValueChange={value => value && setMetric(value as FlowMetric)}
					size="sm"
					className="gap-0 overflow-hidden rounded-md border bg-background"
				>
					{METRIC_OPTIONS.map(option => (
						<ToggleGroupItem
							key={option.value}
							value={option.value}
							className="border-l px-3 text-xs first:border-l-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
						>
							{option.label}
						</ToggleGroupItem>
					))}
				</ToggleGroup>
			</div>

			<div className="absolute bottom-3 left-3 z-10 flex flex-col gap-1 rounded-lg border bg-background/95 p-2 text-[11px] shadow backdrop-blur">
				{(Object.keys(STATUS_STYLE) as SummaryValidationStatus[]).map(status => (
					<div key={status} className="flex items-center gap-2">
						<span
							className="inline-block h-3 w-3 rounded-full"
							style={{ border: `2px solid ${STATUS_STYLE[status].color}` }}
						/>
						<span className="text-muted-foreground">{STATUS_STYLE[status].label}</span>
					</div>
				))}
			</div>

			{selectedStructure && (
				<StructureDetailPanel
					iAndI={selectedStructure}
					status={STATUS_STYLE[selectedStatus]}
					missingUpstreamCount={missingUpstreamCount}
					flows={METRIC_OPTIONS.map(option => ({
						label: option.label,
						value: metricFlowValue(selectedStructure, option.value),
						unit: metricFlowUnit(iAndIStructures),
					}))}
				/>
			)}
		</div>
	);
});
